import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import venueApi from '../../lib/venueApi';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { ChevronLeft, ChevronRight, CalendarDays, Loader2, RefreshCw, LayoutDashboard, MapPin } from 'lucide-react';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAY_ABBR = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const isoDate = (d) => d.toISOString().slice(0, 10);
const firstOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const addMonths = (d, n) => new Date(d.getFullYear(), d.getMonth() + n, 1);

// ─── Color logic ───────────────────────────────
// Bookings view (what's blocked):
//   • empty = available (white)
//   • prospect = amber (tentative block)
//   • confirmed = red (hard block) — updated from green to avoid the "green = free" trap
// Availability view (what's free):
//   • all free = green
//   • some free = amber
//   • none free = red

function bookingsCellStyle(prospect, confirmed) {
  if (confirmed && prospect) return 'bg-gradient-to-br from-amber-400 to-red-500 text-white';
  if (confirmed) return 'bg-red-500 text-white hover:bg-red-600';
  if (prospect) return 'bg-amber-300 text-amber-900 hover:bg-amber-400';
  return 'bg-white hover:bg-slate-50 text-slate-700';
}

function availabilityCellStyle(free, total) {
  if (total === 0) return 'bg-slate-50 text-slate-300';
  if (free === 0) return 'bg-red-500 text-white hover:bg-red-600';
  if (free === total) return 'bg-emerald-500 text-white hover:bg-emerald-600';
  return 'bg-amber-300 text-amber-900 hover:bg-amber-400';
}

function MonthGrid({ year, month, mode, dayData, onDayClick, locationFilter }) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startWeekday = firstDay.getDay();
  const daysInMonth = lastDay.getDate();
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <Card className="p-3 border border-border">
      <p className="text-sm font-semibold text-slate-800 mb-2 px-1">{MONTH_NAMES[month]} {year}</p>
      <div className="grid grid-cols-7 gap-0.5 mb-1">
        {DAY_ABBR.map((d, i) => (
          <div key={i} className="text-[9px] uppercase text-slate-400 text-center py-1 font-medium">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {cells.map((day, idx) => {
          if (day === null) return <div key={idx} className="h-11" />;
          const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const info = dayData[dateStr];
          const isToday = dateStr === isoDate(new Date());

          let bgCls = 'bg-white hover:bg-slate-50 text-slate-700';
          let content = null;
          let clickable = false;

          if (mode === 'bookings') {
            const entries = info?.entries || [];
            const prospect = entries.some((e) => e.status === 'prospect');
            const confirmed = entries.some((e) => e.status === 'confirmed');
            bgCls = bookingsCellStyle(prospect, confirmed);
            clickable = entries.length > 0;
            content = entries.length > 0 ? <span className="text-[9px] leading-none mt-0.5 opacity-90">{entries.length}</span> : null;
          } else {
            // availability
            const stats = locationFilter === 'all' ? info?.overall : info?.by_location?.[locationFilter];
            if (!stats || stats.total === 0) {
              bgCls = 'bg-slate-50 text-slate-300';
            } else {
              bgCls = availabilityCellStyle(stats.free, stats.total);
              clickable = true;
              content = <span className="text-[9px] leading-none mt-0.5 opacity-90 font-medium">{stats.free}/{stats.total}</span>;
            }
          }

          return (
            <button
              key={idx}
              onClick={() => clickable && onDayClick(dateStr)}
              data-testid={`venue-cal-day-${dateStr}`}
              className={`relative h-11 rounded-md border ${isToday ? 'border-violet-500 ring-1 ring-violet-200' : 'border-slate-100'} ${bgCls} text-[11px] font-medium flex flex-col items-center justify-center transition-all ${clickable ? 'cursor-pointer' : 'cursor-default'}`}
            >
              <span className="leading-none">{day}</span>
              {content}
            </button>
          );
        })}
      </div>
    </Card>
  );
}

export default function VenueCalendarPage() {
  const [startDate, setStartDate] = useState(firstOfMonth(new Date()));
  const [months, setMonths] = useState(6);
  const [mode, setMode] = useState('bookings'); // 'bookings' | 'availability'
  const [bookingsData, setBookingsData] = useState(null);
  const [availabilityData, setAvailabilityData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dayModal, setDayModal] = useState(null);
  const [locations, setLocations] = useState([]);
  const [filterLocation, setFilterLocation] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const fetchData = () => {
    setLoading(true);
    const params = { months, start: isoDate(startDate) };
    Promise.allSettled([
      venueApi.get('/calendar', { params }),
      venueApi.get('/availability', { params }),
    ]).then(([bR, aR]) => {
      if (bR.status === 'fulfilled') setBookingsData(bR.value.data);
      if (aR.status === 'fulfilled') setAvailabilityData(aR.value.data);
      if (bR.status === 'rejected') toast.error('Failed to load bookings');
    }).finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, [startDate, months]);

  useEffect(() => {
    venueApi.get('/locations').then((r) => setLocations(r.data.locations || [])).catch(() => {});
  }, []);

  // Merge bookings + availability into a unified per-day map
  const dayData = useMemo(() => {
    const map = {};
    if (bookingsData) {
      for (const d of bookingsData.days) {
        let entries = d.entries;
        if (filterLocation !== 'all') entries = entries.filter((e) => e.location_id === filterLocation);
        if (filterStatus !== 'all') entries = entries.filter((e) => e.status === filterStatus);
        map[d.date] = { ...map[d.date], entries };
      }
    }
    if (availabilityData) {
      for (const d of availabilityData.days) {
        map[d.date] = { ...map[d.date], overall: d.overall, by_location: d.by_location };
      }
    }
    return map;
  }, [bookingsData, availabilityData, filterLocation, filterStatus]);

  const months_list = useMemo(() => {
    const arr = [];
    for (let i = 0; i < months; i++) {
      const d = addMonths(startDate, i);
      arr.push({ year: d.getFullYear(), month: d.getMonth() });
    }
    return arr;
  }, [startDate, months]);

  // Per-location summary (aggregate across the whole window)
  const locationSummary = useMemo(() => {
    if (!availabilityData) return [];
    const rows = (availabilityData.locations || []).map((loc) => {
      let freeDays = 0;
      let fullyBookedDays = 0;
      let partialDays = 0;
      for (const d of availabilityData.days) {
        const s = d.by_location?.[loc.id];
        if (!s || s.total === 0) continue;
        if (s.free === s.total) freeDays += 1;
        else if (s.free === 0) fullyBookedDays += 1;
        else partialDays += 1;
      }
      const total = availabilityData.days.length;
      return {
        ...loc,
        total_days: total,
        free_days: freeDays,
        partial_days: partialDays,
        fully_booked_days: fullyBookedDays,
        availability_pct: total ? Math.round((freeDays / total) * 100) : 0,
      };
    });
    return rows;
  }, [availabilityData]);

  const handleStatusToggle = async (entry) => {
    const next = entry.status === 'prospect' ? 'confirmed' : 'prospect';
    try {
      await venueApi.patch(`/requirements/${entry.id}`, { status: next });
      toast.success(`Marked as ${next}`);
      fetchData();
      if (dayModal) {
        const updated = {
          ...dayModal,
          entries: (dayModal.entries || []).map((e) => e.id === entry.id ? { ...e, status: next } : e),
        };
        setDayModal(updated);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const openDayModal = (dateStr) => {
    const info = dayData[dateStr] || {};
    setDayModal({ date: dateStr, entries: info.entries || [], overall: info.overall, by_location: info.by_location });
  };

  return (
    <div className="space-y-5" data-testid="venue-calendar-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-violet-600" /> Venue Calendar
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            {mode === 'bookings'
              ? 'What is blocked — red = confirmed, amber = prospect, empty = available.'
              : 'What is free — green = all labs available, amber = some booked, red = fully booked. Cell shows free/total.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" className="h-8 rounded-md" onClick={() => setStartDate(addMonths(startDate, -months))} data-testid="venue-cal-prev">
            <ChevronLeft className="w-3.5 h-3.5" />
          </Button>
          <Button size="sm" variant="outline" className="h-8 rounded-md" onClick={() => setStartDate(firstOfMonth(new Date()))} data-testid="venue-cal-today">
            Today
          </Button>
          <Button size="sm" variant="outline" className="h-8 rounded-md" onClick={() => setStartDate(addMonths(startDate, months))} data-testid="venue-cal-next">
            <ChevronRight className="w-3.5 h-3.5" />
          </Button>
          <Button size="sm" variant="ghost" className="h-8 rounded-md" onClick={fetchData} data-testid="venue-cal-refresh">
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {/* View toggle + filters */}
      <Card className="p-3 border border-border bg-slate-50/50 flex items-center flex-wrap gap-2">
        <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5" role="tablist">
          <button
            data-testid="venue-mode-bookings"
            onClick={() => setMode('bookings')}
            className={`text-xs px-3 py-1.5 rounded ${mode === 'bookings' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'} transition`}
          >
            <CalendarDays className="w-3 h-3 inline mr-1" /> Bookings
          </button>
          <button
            data-testid="venue-mode-availability"
            onClick={() => setMode('availability')}
            className={`text-xs px-3 py-1.5 rounded ${mode === 'availability' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'} transition`}
          >
            <LayoutDashboard className="w-3 h-3 inline mr-1" /> Availability
          </button>
        </div>

        <Select value={String(months)} onValueChange={(v) => setMonths(Number(v))}>
          <SelectTrigger className="h-8 w-32 text-xs" data-testid="venue-cal-months"><SelectValue /></SelectTrigger>
          <SelectContent>
            {[3, 6, 9, 12].map((m) => <SelectItem key={m} value={String(m)}>{m} months</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={filterLocation} onValueChange={setFilterLocation}>
          <SelectTrigger className="h-8 w-40 text-xs" data-testid="venue-cal-loc-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Locations</SelectItem>
            {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
          </SelectContent>
        </Select>

        {mode === 'bookings' && (
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="h-8 w-36 text-xs" data-testid="venue-cal-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="prospect">Prospect</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
            </SelectContent>
          </Select>
        )}

        <div className="flex-1" />

        <div className="flex items-center gap-3 text-[11px] text-slate-500">
          {mode === 'bookings' ? (
            <>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-amber-300" /> Prospect</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-red-500" /> Confirmed</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded border border-slate-200" /> Available</span>
            </>
          ) : (
            <>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-emerald-500" /> All free</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-amber-300" /> Some free</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-red-500" /> None free</span>
            </>
          )}
        </div>
      </Card>

      {/* Location-wise summary (visible in availability mode; shown in bookings mode too as at-a-glance) */}
      {locationSummary.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-2" data-testid="venue-cal-location-summary">
          {locationSummary.map((loc) => (
            <button
              key={loc.id}
              onClick={() => setFilterLocation(filterLocation === loc.id ? 'all' : loc.id)}
              data-testid={`venue-cal-locsum-${loc.id}`}
              className={`text-left p-3 rounded-lg border transition ${filterLocation === loc.id ? 'border-violet-400 ring-2 ring-violet-100 bg-white' : 'border-slate-200 bg-white hover:border-slate-300'}`}
            >
              <div className="flex items-center gap-1.5 mb-1.5">
                <MapPin className="w-3.5 h-3.5 text-violet-600" />
                <p className="text-sm font-semibold text-slate-800">{loc.name}</p>
                <span className="text-[10px] text-slate-400 ml-auto">{loc.total_labs} lab{loc.total_labs === 1 ? '' : 's'}</span>
              </div>
              <div className="flex h-2 rounded-full overflow-hidden bg-slate-100">
                <div className="bg-emerald-500 h-full" style={{ width: `${(loc.free_days / (loc.total_days || 1)) * 100}%` }} />
                <div className="bg-amber-300 h-full" style={{ width: `${(loc.partial_days / (loc.total_days || 1)) * 100}%` }} />
                <div className="bg-red-500 h-full" style={{ width: `${(loc.fully_booked_days / (loc.total_days || 1)) * 100}%` }} />
              </div>
              <div className="flex items-center gap-3 mt-1.5 text-[10px] text-slate-500">
                <span className="text-emerald-700 font-medium">{loc.free_days} free</span>
                <span className="text-amber-700">{loc.partial_days} partial</span>
                <span className="text-red-700">{loc.fully_booked_days} full</span>
                <span className="ml-auto text-slate-400">of {loc.total_days}d</span>
              </div>
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-violet-600" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {months_list.map((m) => (
            <MonthGrid
              key={`${m.year}-${m.month}`}
              year={m.year}
              month={m.month}
              mode={mode}
              dayData={dayData}
              locationFilter={filterLocation}
              onDayClick={openDayModal}
            />
          ))}
        </div>
      )}

      <Dialog open={!!dayModal} onOpenChange={(o) => !o && setDayModal(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="venue-day-modal">
          <DialogHeader>
            <DialogTitle>
              {dayModal?.date && new Date(dayModal.date + 'T00:00:00').toLocaleDateString('en-IN', {
                weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
              })}
            </DialogTitle>
          </DialogHeader>

          {/* Availability summary block */}
          {dayModal && (
            <div className="grid grid-cols-3 gap-2 text-center mb-2" data-testid="venue-day-availability">
              {(() => {
                const stats = filterLocation === 'all' ? dayModal.overall : dayModal.by_location?.[filterLocation];
                if (!stats) return null;
                return (
                  <>
                    <div className="bg-emerald-50 border border-emerald-100 rounded-md py-2">
                      <p className="text-[10px] uppercase text-emerald-700 font-medium">Free</p>
                      <p className="text-lg font-bold text-emerald-800">{stats.free}<span className="text-xs text-emerald-500 font-normal">/{stats.total}</span></p>
                    </div>
                    <div className="bg-amber-50 border border-amber-100 rounded-md py-2">
                      <p className="text-[10px] uppercase text-amber-700 font-medium">Prospect</p>
                      <p className="text-lg font-bold text-amber-800">{stats.prospect_only}</p>
                    </div>
                    <div className="bg-red-50 border border-red-100 rounded-md py-2">
                      <p className="text-[10px] uppercase text-red-700 font-medium">Confirmed</p>
                      <p className="text-lg font-bold text-red-800">{stats.confirmed}</p>
                    </div>
                  </>
                );
              })()}
            </div>
          )}

          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {(dayModal?.entries || []).length === 0 ? (
              <p className="text-xs text-slate-400 text-center py-6">No bookings on this day.</p>
            ) : (dayModal.entries.map((e) => (
              <div key={e.id} className="border border-slate-200 rounded-lg p-3 flex items-start justify-between gap-3" data-testid={`venue-day-entry-${e.id}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Badge className={e.status === 'confirmed' ? 'bg-red-100 text-red-800 border-red-200' : 'bg-amber-100 text-amber-800 border-amber-200'}>{e.status}</Badge>
                    <span className="text-sm font-semibold text-slate-900">{e.company_name}</span>
                  </div>
                  <p className="text-[12px] text-slate-500 mt-1">
                    Lab {e.lab_number} · {e.location_name}
                    {e.total_strength ? ` · ${e.total_strength} pax` : ''}
                  </p>
                  <Link to={`/venue/requirements/${e.id}`} className="text-[11px] text-violet-600 hover:underline mt-1 inline-block" data-testid={`venue-drilldown-${e.id}`}>
                    View details →
                  </Link>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-[11px] rounded-md"
                  onClick={() => handleStatusToggle(e)}
                  data-testid={`venue-toggle-${e.id}`}
                >
                  {e.status === 'prospect' ? 'Confirm' : 'Mark Prospect'}
                </Button>
              </div>
            )))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
